'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import { getColorClassnameByValue } from '@benzinga/frontend-utils';
import { formatPercentage, formatPrice } from '@benzinga/utils/numberUtils';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { Collapse, Icon } from '@benzinga/core-ui';
import classNames from 'classnames';
import { Ticker, TickerLink } from '@benzinga/ticker-ui';
import { DateTime } from 'luxon';

const commodities = [
  { name: 'S&P', symbol: 'SPY', type: 'stock' },
  { name: 'Dow Jones', symbol: 'DIA', type: 'stock' },
  { name: 'Tech', symbol: 'QQQ', type: 'stock' },
  { name: 'Oil', symbol: 'USO', type: 'commodity' },
  { name: 'Gold', symbol: 'GLD', type: 'commodity' },
  { name: 'Treasuries', symbol: 'TLT', type: 'commodity' },
];

const isStock = (symbol: string) => {
  const type = commodities.find(commodity => commodity.symbol === symbol)?.type;
  if (type === 'stock') return true;
  return false;
};

export interface StockCommoditiesProps {
  quotes: DelayedQuote[];
}

interface StockCommoditiesData extends DelayedQuote {
  symbol: string;
  level: string;
  fairValue: string;
  difference: string;
}

export const StockCommodities: React.FC<StockCommoditiesProps> = ({ quotes }) => {
  const stockAndCommoditiesData: StockCommoditiesData[] = React.useMemo(() => {
    if (!Array.isArray(quotes)) return [];
    return quotes.map(quote => {
      const data = {
        ...quote,
        difference: quote.change ? formatPrice(quote.change, { fixedPlace: 2, showNearZero: false }) : 'TBA',
        fairValue: quote.close ? formatPrice(quote.close, { fixedPlace: 2, showNearZero: false }) : 'TBA',
        level: quote.previousClosePrice
          ? formatPrice(quote.previousClosePrice, { fixedPlace: 2, showNearZero: false })
          : 'TBA',
        symbol: quote.symbol,
      };
      return data;
    });
  }, [quotes]);

  const [activeStock, setActiveStock] = React.useState<StockCommoditiesData | null>(() => {
    const item = stockAndCommoditiesData?.[0];
    if (!isStock(item?.symbol)) return null;
    return item;
  });

  const handleSetActiveExpandedStock = React.useCallback((item: StockCommoditiesData) => {
    if (!isStock(item.symbol)) return;
    setActiveStock(item);
  }, []);

  React.useEffect(() => {
    if (stockAndCommoditiesData && stockAndCommoditiesData.length > 0) {
      handleSetActiveExpandedStock(stockAndCommoditiesData[0]);
    }
  }, [handleSetActiveExpandedStock, stockAndCommoditiesData]);

  const lastUpdatedAt = stockAndCommoditiesData?.[stockAndCommoditiesData?.length - 1]?.lastTradeTime;
  const updateTime = lastUpdatedAt
    ? DateTime.fromMillis(lastUpdatedAt).setZone('America/New_York').toFormat('hh:mma ZZZZ')
    : null;

  return (
    <Container className="stock-commodities-container">
      <div className="header-container stock-commodities flex">
        <strong className="font-bold text-black text-base">Stock And Commodities</strong>
      </div>
      <ul className="stocks-and-commodities-list">
        {stockAndCommoditiesData.map(item => {
          const name = commodities.find(commodity => commodity.symbol === item.symbol)?.name;
          const isOpened = isStock(item.symbol) && activeStock?.symbol === item.symbol;

          return (
            <li className={isStock(item.symbol) ? 'type-stock' : undefined} key={item.symbol}>
              <Collapse
                defaultCollapsed={!isOpened}
                header={
                  <div className="list-item-info">
                    <Ticker
                      symbol={item.symbol}
                      targetElement={
                        <TickerLink name={name} symbol={item.symbol}>
                          <div className={`name ${getColorClassnameByValue(item.change)}`}>{name}</div>
                        </TickerLink>
                      }
                    />
                    <div className={`value ${getColorClassnameByValue(item.change)}`}>
                      {isStock(item.symbol) && (
                        <>
                          <span className="change-value">{item.difference}</span>
                          <span className="divider">/</span>
                        </>
                      )}
                      <span className="change-percent-value">
                        {item.changePercent ? `${formatPercentage(item.changePercent)}%` : 'TBA'}
                      </span>
                    </div>
                    {isStock(item.symbol) && (
                      <div className={classNames('caret-down-icon', { active: isOpened })}>
                        <Icon icon={faCaretDown} />
                      </div>
                    )}
                  </div>
                }
                hideHeaderIcon={true}
                onToggle={() => {
                  if (item.symbol === activeStock?.symbol) return false;
                  handleSetActiveExpandedStock(item);
                  return true;
                }}
              >
                <div className="list-item-info-dropdown">
                  <div>
                    <span className="label">Level</span>
                    <span className="value">{item.level}</span>
                  </div>
                  <div>
                    <span className="label">Fair Value</span>
                    <span className="value">{item.fairValue}</span>
                  </div>
                  <div>
                    <span className="label">Difference</span>
                    <span className={`value ${getColorClassnameByValue(item.change)}`}>{item.difference}</span>
                  </div>
                </div>
              </Collapse>
            </li>
          );
        })}
      </ul>
      <div className="update-time-container">
        {updateTime && <div className="update-time">Data as of {updateTime}</div>}
      </div>
    </Container>
  );
};

const Container = styled.div`
  &.stock-commodities-container {
    li .list-item-info,
    .list-item-info-dropdown {
      .value {
        &.red {
          color: ${({ theme }) => theme.colorPalette.red500};
        }
        &.green {
          color: ${({ theme }) => theme.colorPalette.green500};
        }
      }
    }

    .stocks-and-commodities-list {
      .collapse-panel-header {
        padding: 0;
        background-color: unset;
      }

      @media (max-width: 800px) {
        .collapse-panel-header .list-item-info {
          height: 38px;
        }
        .list-item-info-dropdown > div {
          height: 32px;
        }
      }

      li .list-item-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: ${({ theme }) => theme.fontSize.sm};
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        padding: 4px 0;
        border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray100};

        .caret-down-icon {
          font-size: 9px;
          &.active {
            transform: rotate(180deg);
          }
        }

        .name {
          color: ${({ theme }) => theme.colorPalette.blue800};

          &.red {
            &:before {
              border-top-color: #c00;
            }
          }

          &.green {
            &:before {
              border-top-color: #090;
              transform: rotate(180deg);
            }
          }

          &:before {
            content: '';
            width: 0;
            height: 0;
            border-left: 3.5px solid transparent;
            border-right: 3.5px solid transparent;
            vertical-align: middle;
            border-top: 4px solid #000;
            display: inline-block;
            margin-right: 3px;
          }
        }

        .value {
          margin-left: auto;
          padding-right: 3px;

          .divider {
            margin: 0 4px;
          }
        }
      }
      .type-stock {
        .list-item-info {
          cursor: pointer;
        }
      }
    }

    .list-item-info-dropdown {
      font-size: ${({ theme }) => theme.fontSize.sm};
      > div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray100};
        padding: 2px 0;
      }
    }

    .update-time-container {
      height: 15px;
    }

    .update-time {
      font-size: ${({ theme }) => theme.fontSize.xs};
      text-align: right;
      width: 100%;
      margin-top: 2px;
    }
  }
`;
